import { ChangeDetectionStrategy, Component, EventEmitter, inject, OnDestroy, Output } from '@angular/core';
import { FeedbackService, NotificationService } from '@app/core';
import { Answer, Attachment, Feedback, TypeFeedbackEnum } from '@app/core/models';
import { notNull, safeEmpty } from '@app/shared/utils';
import { BehaviorSubject, finalize, map, Observable, Subject, takeUntil } from 'rxjs';
import { ModalReviewService } from './modal-review.service';
import { SHARED_MODULES } from '@app/shared';
import { ModalReviewFeedbackTextComponent } from './modal-review-feedback-text.component';
import { ModalReviewFeedbackListComponent } from './modal-review-feedback-list.component';
import { ModalReviewFeedbackFileComponent } from './modal-review-feedback-file.component';

enum ModeViewFeedbackEnum {
  List,
  AddText,
  File,
}

@Component({
  selector: 'app-modal-review-feedback',
  imports: [
    SHARED_MODULES,
    ModalReviewFeedbackTextComponent,
    ModalReviewFeedbackListComponent,
    ModalReviewFeedbackFileComponent,
  ],
  templateUrl: './modal-review-feedback.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalReviewFeedbackComponent implements OnDestroy {
  private readonly _feedbackService = inject(FeedbackService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _modalReviewService = inject(ModalReviewService);
  @Output() public fileView = new EventEmitter();
  public readonly modeViewFeedbackEnum = ModeViewFeedbackEnum;
  public modeViewFeedback$ = new BehaviorSubject<ModeViewFeedbackEnum>(ModeViewFeedbackEnum.List);
  public feedbackSelected$ = new BehaviorSubject<Feedback>(undefined);
  public destroy$ = new Subject<void>();
  public answer$ = new BehaviorSubject<Answer>(undefined);

  constructor() {
    this._modalReviewService.answers$
      .pipe(
        notNull(),
        takeUntil(this.destroy$),
        map((answers) => answers.find((answer) => answer.is_selected)),
      )
      .subscribe((answer) => this.answer$.next(answer));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.modeViewFeedback$.complete();
    this.feedbackSelected$.complete();
  }

  public onChangeModeViewFeedback(modeViewFeedback: ModeViewFeedbackEnum) {
    this.modeViewFeedback$.next(modeViewFeedback);
  }

  public onEditFeedback(feedback: Feedback) {
    if (feedback.type === TypeFeedbackEnum.Text) {
      this.feedbackSelected$.next(feedback);
      this.modeViewFeedback$.next(ModeViewFeedbackEnum.AddText);
      return;
    }

    this.fileView.emit(feedback.attachments[0]);
  }

  public onRemoveFeedback(feedback: Feedback) {
    this._modalReviewService.removeFeedback(feedback);
  }

  public onCloseFeedback(feedback: Feedback) {
    this.modeViewFeedback$.next(this.modeViewFeedbackEnum.List);
    this.feedbackSelected$.next(undefined);
    if (!feedback) {
      return;
    }

    if (!feedback?.id) {
      this.storeFeedback({ ...feedback, answer_id: this.answer$.value.id });
      return;
    }

    this.observable(this._feedbackService.update(feedback.id, feedback), (data) =>
      this._modalReviewService.editFeedback(data),
    );
  }

  public onCloseModeViewFeedbackFile(file: Attachment) {
    if (!!file) {
      this.storeFeedback({
        type: TypeFeedbackEnum.File,
        answer_id: this.answer$.value.id,
        attachments: [file],
      });
    }

    this.modeViewFeedback$.next(this.modeViewFeedbackEnum.List);
  }

  private observable(observable: Observable<any>, callback?: any) {
    observable
      .pipe(
        takeUntil(this.destroy$),
        safeEmpty(),
        finalize(() => this._notificationService.success('geral.sucesso')),
      )
      .subscribe(callback);
  }

  private storeFeedback(feedback: any) {
    this.observable(this._feedbackService.store(feedback), (data) => {
      this._modalReviewService.addFeedback(data);
    });
  }
}
